/**
 * Parse structured individual-summary JSON returned as plain text from the API.
 * Used to render a points-first digest instead of raw JSON in the UI.
 */

export interface DigestPoint {
  text: string;
  topic?: string | null;
  pageLabel?: string | null;
  excerpt?: string | null;
}

export interface ParsedIndividualSummary {
  entity?: string | null;
  filename?: string | null;
  totalPages?: number | null;
  points: DigestPoint[];
  verificationSummary?: string | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function pageLabelFromAttribution(att: unknown): string | null {
  if (!isRecord(att)) return null;
  // .docx-sourced points cite by paragraph; we flag the Word-doc origin so
  // attorneys know pagination isn't canonical and can search by paragraph.
  const isParagraph = att.unit === 'paragraph';
  const primary = att.primary_page;
  const primaryNum =
    typeof primary === 'number' && Number.isFinite(primary) ? primary : null;
  const rangesRaw = att.page_ranges;
  const rangeNums = Array.isArray(rangesRaw)
    ? rangesRaw.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
    : [];

  // Union primary + ranges, dedup, sort ascending. When the position is
  // discussed across multiple pages (e.g. page 3 introduces, page 4 has the
  // bulk), we surface all of them with the primary highlighted so the
  // attorney lands on the right page first.
  const unionSet = new Set<number>(rangeNums);
  if (primaryNum !== null) unionSet.add(primaryNum);
  const union = Array.from(unionSet).sort((a, b) => a - b);

  if (union.length === 0) return null;
  if (union.length === 1) {
    const p = union[0];
    return isParagraph ? `¶ ${p} (Word doc)` : `Page ${p}`;
  }
  // Multi-page: render "Pages 3, 4 (primarily 4)" so the user knows the
  // citation is multi-page and where the bulk lives. When we don't know
  // the primary (rare — older summaries without primary_page), just list.
  const listed = union.join(', ');
  if (primaryNum !== null) {
    return isParagraph
      ? `¶¶ ${listed} (primarily ¶ ${primaryNum}; Word doc)`
      : `Pages ${listed} (primarily ${primaryNum})`;
  }
  return isParagraph ? `¶¶ ${listed} (Word doc)` : `Pages ${listed}`;
}

/**
 * Returns a digest model when `raw` is JSON with a top-level `points` array
 * of objects that include a string `point` field. Otherwise returns null.
 */
export function tryParseIndividualSummaryJson(raw: string): ParsedIndividualSummary | null {
  const trimmed = raw?.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }

  if (!isRecord(parsed)) return null;
  const pointsRaw = parsed.points;
  if (!Array.isArray(pointsRaw) || pointsRaw.length === 0) return null;

  const points: DigestPoint[] = [];
  for (const item of pointsRaw) {
    if (!isRecord(item)) continue;
    const text = item.point;
    if (typeof text !== 'string' || !text.trim()) continue;
    const topic = typeof item.topic === 'string' ? item.topic : null;
    const excerpt =
      typeof item.source_text === 'string' && item.source_text.trim()
        ? item.source_text
        : typeof item.source_context === 'string' && item.source_context.trim()
          ? item.source_context
          : null;
    const pageLabel = pageLabelFromAttribution(item.page_attribution);
    points.push({
      text: text.trim(),
      topic: topic?.trim() || null,
      pageLabel,
      excerpt: excerpt?.trim() || null,
    });
  }

  if (points.length === 0) return null;

  const entity = typeof parsed.entity === 'string' ? parsed.entity.trim() : null;
  const filename = typeof parsed.filename === 'string' ? parsed.filename.trim() : null;
  const totalPages =
    typeof parsed.total_pages === 'number' && Number.isFinite(parsed.total_pages)
      ? parsed.total_pages
      : null;

  let verificationSummary: string | null = null;
  const vr = parsed.verification_result;
  if (isRecord(vr)) {
    const total = typeof vr.total_points === 'number' ? vr.total_points : null;
    const validated = typeof vr.validated_points === 'number' ? vr.validated_points : null;
    const avg =
      typeof vr.average_confidence === 'number' && Number.isFinite(vr.average_confidence)
        ? Math.round(vr.average_confidence * 100) / 100
        : null;
    if (total != null && validated != null) {
      verificationSummary =
        avg != null
          ? `Verification: ${validated}/${total} points validated (avg confidence ${avg}).`
          : `Verification: ${validated}/${total} points validated.`;
    }
  }

  return {
    entity: entity || null,
    filename: filename || null,
    totalPages,
    points,
    verificationSummary,
  };
}

/** Plain-text export for "Copy digest" / download. */
export function formatDigestPlainText(digest: ParsedIndividualSummary): string {
  const lines: string[] = [];
  if (digest.entity) lines.push(`Filer: ${digest.entity}`);
  if (digest.filename) lines.push(`File: ${digest.filename}`);
  if (digest.totalPages != null) lines.push(`Pages: ${digest.totalPages}`);
  if (digest.verificationSummary) lines.push(digest.verificationSummary);
  if (lines.length) lines.push('');
  digest.points.forEach((p, i) => {
    lines.push(`${i + 1}. ${p.text}`);
    if (p.topic && p.topic !== 'General document analysis and key points extraction') {
      lines.push(`   Topic: ${p.topic}`);
    }
    if (p.pageLabel) lines.push(`   ${p.pageLabel}`);
    if (p.excerpt) {
      lines.push(`   Excerpt: ${p.excerpt}`);
    }
    lines.push('');
  });
  return lines.join('\n').trim();
}
